import { useClipboard } from '@crow/ui/copy-button';
import { useCallback } from 'react';

/** How long the "copied" acknowledgement stays visible before it resets. */
export const COPIED_RESET_MS = 1500;

/** Sentinel id for the common single-target case (`copy(text)` with no id). */
const DEFAULT_COPY_ID = 'copied';

export type UseCopyToClipboardResult = {
  /**
   * The id of the value copied most recently, or `null` once the acknowledgement
   * has reset. Truthy while a copy is being acknowledged, so single-target
   * callers can treat it as a boolean.
   */
  readonly copied: string | null;
  /** Write `text` to the clipboard and flag `id` (default sentinel) as copied. */
  readonly copy: (text: string, id?: string) => Promise<void>;
};

/**
 * Copy-to-clipboard with a self-resetting acknowledgement: the design system's
 * `useClipboard` (`@crow/ui`), with Crowdex's {@link COPIED_RESET_MS} timeout.
 * A refused copy (the host webview may deny clipboard access) leaves the state
 * untouched.
 *
 * Pass an `id` when a component copies several distinct values and needs to know
 * which one was copied (compare `copied === id`); omit it for the single-target
 * case and treat `copied` as a boolean.
 */
export function useCopyToClipboard(): UseCopyToClipboardResult {
  const { copiedId, copy } = useClipboard(COPIED_RESET_MS);
  const copyText = useCallback(
    async (text: string, id: string = DEFAULT_COPY_ID) => {
      await copy(text, id);
    },
    [copy],
  );
  return { copied: copiedId, copy: copyText };
}
